// src/components/Project/ProjectList.js
import React, { useContext, useState, useEffect, useMemo } from "react";
import HeaderProject from "./HeaderProject";
import { ProjectContext } from "../../context/ProjectProvider";
import { updateProjectAPI, deleteProjectAPI } from "../../utils/api";
import "./ProjectList.css";

/**
 * Lista de proyectos agrupados por fecha:
 *  • Today
 *  • Yesterday
 *  • Previous 7 Days
 *  • Previous 30 Days
 *  • Meses anteriores (Enero 2025, Diciembre 2024…)
 */
const ProjectList = () => {
  const {
    /* proyectos */
    projects,
    setProjects,
    selectedProject,
    setSelectedProject,
    setCurrentProject,

    /* panes y flags */
    setCenterCollapsed,
    setPdfColumnCollapsed,

    hasMoreProjects,
    loadMoreProjects,
    isLoadingMoreProjects,
    loadProjectDetails,
    confirm,
    isInitialLoading,
    loadingActions,
    setLoadingActions,
  } = useContext(ProjectContext);

  const [openOptionsFor, setOpenOptionsFor] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editingValue, setEditingValue] = useState("");

  const setProjectActionLoading = (projectId, isLoading) =>
    setLoadingActions((prev) => ({ ...prev, [projectId]: isLoading }));

  /* ───────────────────────────────────────────────
     Sincroniza colapso de la columna central
  ──────────────────────────────────────────────── */
  useEffect(() => {
    setCenterCollapsed(!selectedProject);
  }, [selectedProject, setCenterCollapsed]);

  /* ───────────────────────────────────────────────
     Handlers de selección
  ──────────────────────────────────────────────── */
  const handleClick = async (proj) => {
    // Marca inmediatamente como seleccionado
    setSelectedProject({
      id: proj.id,
      name: proj.name,
      created_at: proj.created_at,
    });

    setProjectActionLoading(proj.id, true);
    try {
      await loadProjectDetails(proj.id);
    } catch (err) {
      console.error(err);
    } finally {
      setProjectActionLoading(proj.id, false);
    }
  };

  /* ───────────────────────────────────────────────
     Renombrar proyecto
  ──────────────────────────────────────────────── */
  const toggleOptionsMenu = (projectId) =>
    setOpenOptionsFor((curr) => (curr === projectId ? null : projectId));

  const handleClickRename = (project) => {
    setEditingId(project.id);
    setEditingValue(project.name.trim() || "(Untitled)");
    setOpenOptionsFor(null);
  };

  const handleSubmitEdit = async (projectId) => {
    setProjectActionLoading(projectId, true);
    try {
      const { project } = await updateProjectAPI(projectId, {
        name: editingValue,
      });
      setProjects((prev) =>
        prev.map((p) => (p.id === projectId ? project : p))
      );
    } catch (err) {
      console.error("Error updating the project", err);
    } finally {
      setEditingId(null);
      setEditingValue("");
      setProjectActionLoading(projectId, false);
    }
  };

  const handleKeyPress = (e, projectId) => {
    if (e.key === "Enter") handleSubmitEdit(projectId);
  };

  /* ───────────────────────────────────────────────
     Eliminar proyecto
  ──────────────────────────────────────────────── */
  const handleClickDelete = async (project) => {
    const confirmed = await confirm({
      title: "Delete project?",
      message: `Are you sure you want to delete the project "${project.name}"? This action cannot be undone.`,
    });
    if (!confirmed) return;

    try {
      await deleteProjectAPI(project.id);
      setProjects((prev) => prev.filter((p) => p.id !== project.id));

      /* Si era el proyecto seleccionado, limpiamos todo
         para que sólo se muestre la drop-zone del PDF.   */
      if (selectedProject?.id === project.id) {
        setSelectedProject(null);
        setCurrentProject(null);
        setCenterCollapsed(true);   // oculta la columna central
        setPdfColumnCollapsed(false); // asegura que col-der se muestre
        // Opcional: volver arriba de la página
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    } catch (err) {
      console.error("Error deleting the project", err);
    } finally {
      setOpenOptionsFor(null);
    }
  };

  /* ───────────────────────────────────────────────
     Cierra menú contextual al hacer clic fuera
  ──────────────────────────────────────────────── */
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (
        !e.target.closest(".project-options-menu") &&
        !e.target.closest(".project-options-btn")
      ) {
        setOpenOptionsFor(null);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  /* ───────────────────────────────────────────────
     Agrupa proyectos por fecha
  ──────────────────────────────────────────────── */
  const groupByDate = useMemo(() => {
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);

    const groups = {
      today: [],
      yesterday: [],
      prev7: [],
      prev30: [],
      months: {},
    };

    projects.forEach((p) => {
      const d = new Date(p.created_at);
      const projDay = new Date(d.getFullYear(), d.getMonth(), d.getDate());
      const diffDays = Math.floor(
        (todayStart - projDay) / (1000 * 60 * 60 * 24)
      );

      if (diffDays === 0) {
        groups.today.push(p);
      } else if (diffDays === 1) {
        groups.yesterday.push(p);
      } else if (diffDays <= 7) {
        groups.prev7.push(p);
      } else if (diffDays <= 30) {
        groups.prev30.push(p);
      } else {
        const monthKey = d.toLocaleString("default", {
          month: "long",
          year: "numeric",
        });
        if (!groups.months[monthKey]) groups.months[monthKey] = [];
        groups.months[monthKey].push(p);
      }
    });

    const sortDesc = (arr) =>
      arr.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    groups.today = sortDesc(groups.today);
    groups.yesterday = sortDesc(groups.yesterday);
    groups.prev7 = sortDesc(groups.prev7);
    groups.prev30 = sortDesc(groups.prev30);
    Object.keys(groups.months).forEach(
      (m) => (groups.months[m] = sortDesc(groups.months[m]))
    );

    return groups;
  }, [projects]);

  /* ───────────────────────────────────────────────
     Render de un proyecto individual
  ──────────────────────────────────────────────── */
  const renderProject = (p) => {
    const isMenuOpen = openOptionsFor === p.id;
    const isSelected = selectedProject?.id === p.id;

    return (
      <li
        key={p.id}
        className={`project-item ${isSelected ? "selected" : ""}`}
        onClick={() => handleClick(p)}
      >
        {editingId === p.id ? (
          <input
            className="project-edit-input"
            type="text"
            value={editingValue}
            onChange={(e) => setEditingValue(e.target.value)}
            onKeyPress={(e) => handleKeyPress(e, p.id)}
            onBlur={() => setEditingId(null)}
            autoFocus
          />
        ) : (
          <span className="project-name">
            {p.name.trim() || "(Untitled)"}
          </span>
        )}

        <div
          className="options-wrapper"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            className="project-options-btn"
            onClick={() => toggleOptionsMenu(p.id)}
            disabled={loadingActions[p.id]}
          >
            {loadingActions[p.id] ? (
              <div className="spinner-border spinner-border-sm" role="status" />
            ) : (
              <i className="bi bi-three-dots-vertical" />
            )}
          </button>
          {isMenuOpen && (
            <div className="project-options-menu">
              <div
                className="project-options-item"
                onClick={() => handleClickRename(p)}
              >
                Rename <i className="bi bi-pencil-square" />
              </div>
              <div
                className="project-options-item"
                onClick={() => handleClickDelete(p)}
              >
                Delete <i className="bi bi-trash" />
              </div>
            </div>
          )}
        </div>
      </li>
    );
  };

  /* ───────────────────────────────────────────────
     Render completo
  ──────────────────────────────────────────────── */
  return (
    <div className="group-container">
      <HeaderProject />

      <ul className="project-list">
        {/* Spinner inicial */}
        {isInitialLoading && (
          <div className="spinner-wrapper">
            <div
              className="spinner-border"
              style={{ width: "2rem", height: "2rem" }}
              role="status"
            />
          </div>
        )}

        {/* Sin proyectos */}
        {!isInitialLoading && projects.length === 0 && (
          <li className="no-projects">No projects found. Upload a PDF.</li>
        )}

        {/* Contenido */}
        {!isInitialLoading && projects.length > 0 && (
          <>
            {/* Encabezado principal */}
            <li className="projects-main-heading">Projects</li>

            {/* Today */}
            {groupByDate.today.length > 0 && (
              <>
                <li className="projects-section-heading">Today</li>
                {groupByDate.today.map(renderProject)}
              </>
            )}

            {/* Yesterday */}
            {groupByDate.yesterday.length > 0 && (
              <>
                <li className="projects-section-heading">Yesterday</li>
                {groupByDate.yesterday.map(renderProject)}
              </>
            )}

            {/* Previous 7 Days */}
            {groupByDate.prev7.length > 0 && (
              <>
                <li className="projects-section-heading">Previous 7 Days</li>
                {groupByDate.prev7.map(renderProject)}
              </>
            )}

            {/* Previous 30 Days */}
            {groupByDate.prev30.length > 0 && (
              <>
                <li className="projects-section-heading">Previous 30 Days</li>
                {groupByDate.prev30.map(renderProject)}
              </>
            )}

            {/* Meses anteriores */}
            {Object.keys(groupByDate.months)
              .sort((a, b) => {
                const [ma, ya] = a.split(" ");
                const [mb, yb] = b.split(" ");
                const da = new Date(`${ma} 1, ${ya}`);
                const db = new Date(`${mb} 1, ${yb}`);
                return db - da; // descendente
              })
              .map((monthKey) => (
                <React.Fragment key={monthKey}>
                  <li className="projects-section-heading">{monthKey}</li>
                  {groupByDate.months[monthKey].map(renderProject)}
                </React.Fragment>
              ))}
          </>
        )}

        {/* Paginación */}
        {hasMoreProjects && projects.length >= 20 && (
          <li className="load-more-wrapper">
            <button
              className="load-more-btn"
              disabled={isLoadingMoreProjects}
              onClick={loadMoreProjects}
            >
              {isLoadingMoreProjects ? "Loading…" : "View more projects"}
            </button>
          </li>
        )}
      </ul>
    </div>
  );
};

export default ProjectList;
